import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  DURATION,
  EASE,
  FOOTER_DELAY,
  STAGGER,
  TEXT_DELAY,
} from "./animation-config.js";

gsap.registerPlugin(ScrollTrigger);

const HEADLINE_X = "50%";
const SUBTITLE_X = "-25vw";
const TEXT_Y = "100%";
const SPLIT_MARK_X = "-100vw";
const MARK_Y = "100%";

const reducedMotion = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
).matches;

const mobileLayout = window.matchMedia("(max-width: 1020px)").matches;

const SPLIT_MARK_LEFT_MOBILE_OPACITY = 0.35;
const SPLIT_MARK_LEFT_MOBILE_X = "-4.71%";

const SCROLL_START_DEFAULT = "top 70%";
const SCROLL_START_FIRST = "top bottom";

const firstTextSection = document.querySelector(".text-section");
const revealChecks = [];

function getScrollStart(section) {
  return section === firstTextSection
    ? SCROLL_START_FIRST
    : SCROLL_START_DEFAULT;
}

function shouldRevealNow(section, start) {
  const top = section.getBoundingClientRect().top;
  const viewportHeight = window.innerHeight;

  if (start === SCROLL_START_FIRST) {
    return top < viewportHeight;
  }

  const match = start.match(/top (\d+)%/);

  if (!match) {
    return false;
  }

  return top <= viewportHeight * (Number(match[1]) / 100);
}

function createScrollTrigger(section, onEnter) {
  let played = false;
  const start = getScrollStart(section);

  const play = () => {
    if (played) {
      return;
    }

    played = true;
    section.classList.add("is-in-view");
    onEnter();
  };

  ScrollTrigger.create({
    trigger: section,
    start,
    once: true,
    onEnter: play,
  });

  revealChecks.push({ section, start, play });

  return play;
}

function runInitialRevealChecks() {
  ScrollTrigger.refresh();

  revealChecks.forEach(({ section, start, play }) => {
    if (shouldRevealNow(section, start)) {
      play();
    }
  });
}

function setDefaultInitialState(overline, headline, subtitle, text, footer) {
  if (overline) {
    gsap.set(overline, { y: TEXT_Y, opacity: 0 });
  }

  if (headline) {
    gsap.set(headline, { x: HEADLINE_X, opacity: 0 });
  }

  if (subtitle) {
    gsap.set(subtitle, { x: SUBTITLE_X, opacity: 0 });
  }

  if (text) {
    gsap.set(text, { y: TEXT_Y, opacity: 0 });
  }

  if (footer) {
    gsap.set(footer, { y: TEXT_Y, opacity: 0 });
  }
}

function initDefaultTextSection(section) {
  const overline = section.querySelector(".text-section__overline");
  const headline = section.querySelector(".text-section__headline");
  const subtitle = section.querySelector(".text-section__subtitle");
  const text = section.querySelector(".text-section__text");
  const footer = section.querySelector(".text-section__footer");

  if (!overline && !headline && !subtitle && !text && !footer) {
    return;
  }

  if (reducedMotion) {
    gsap.set([overline, headline, subtitle, text, footer].filter(Boolean), {
      x: 0,
      y: 0,
      opacity: 1,
    });
    section.classList.add("is-in-view");
    return;
  }

  setDefaultInitialState(overline, headline, subtitle, text, footer);

  createScrollTrigger(section, () => {
    const timeline = gsap.timeline({
      defaults: { duration: DURATION, ease: EASE },
    });

    if (overline) {
      timeline.to(overline, { y: 0, opacity: 1 }, 0);
    }

    if (headline) {
      timeline.to(headline, { x: 0, opacity: 1 }, 0);
    }

    if (subtitle) {
      timeline.to(subtitle, { x: 0, opacity: 1 }, 0);
    }

    if (text) {
      timeline.to(text, { y: 0, opacity: 1 }, TEXT_DELAY);
    }

    if (footer) {
      timeline.to(footer, { y: 0, opacity: 1 }, FOOTER_DELAY);
    }
  });
}

function initSplitMarkRight(section) {
  const overline = section.querySelector(".text-section__overline");
  const headline = section.querySelector(".text-section__headline");
  const subtitle = section.querySelector(".text-section__subtitle");
  const text = section.querySelector(".text-section__text");
  const footer = section.querySelector(".text-section__footer");
  const mark = section.querySelector(".text-section__mark-svg");
  const slideLeftTargets = [overline, headline, subtitle].filter(Boolean);
  const slideUpTargets = [text, footer].filter(Boolean);
  const animatedTargets = [...slideLeftTargets, ...slideUpTargets, mark].filter(
    Boolean,
  );

  if (!animatedTargets.length) {
    return;
  }

  if (reducedMotion) {
    gsap.set([...slideLeftTargets, ...slideUpTargets], {
      x: 0,
      y: 0,
      opacity: 1,
    });

    if (mark) {
      gsap.set(mark, { y: 0, opacity: 1 });
    }

    section.classList.add("is-in-view");
    return;
  }

  slideLeftTargets.forEach((element) => {
    gsap.set(element, { x: SPLIT_MARK_X, opacity: 0 });
  });

  slideUpTargets.forEach((element) => {
    gsap.set(element, { y: TEXT_Y, opacity: 0 });
  });

  if (mark) {
    gsap.set(mark, { y: MARK_Y, opacity: 0 });
  }

  createScrollTrigger(section, () => {
    const timeline = gsap.timeline({
      defaults: { duration: DURATION, ease: EASE },
    });
    let step = 0;

    slideLeftTargets.forEach((element) => {
      timeline.to(element, { x: 0, opacity: 1 }, step * STAGGER);
      step += 1;
    });

    slideUpTargets.forEach((element) => {
      timeline.to(element, { y: 0, opacity: 1 }, step * STAGGER);
      step += 1;
    });

    if (mark) {
      timeline.to(mark, { y: 0, opacity: 1 }, 0);
    }
  });
}

function initSplitMarkLeft(section) {
  const overline = section.querySelector(".text-section__overline");
  const headline = section.querySelector(".text-section__headline");
  const subtitle = section.querySelector(".text-section__subtitle");
  const text = section.querySelector(".text-section__text");
  const footer = section.querySelector(".text-section__footer");
  const mark = section.querySelector(".text-section__mark-svg");
  const slideRightTargets = [overline, headline, subtitle].filter(Boolean);
  const slideUpTargets = [text, footer].filter(Boolean);
  const animateMark = mark && !mobileLayout;
  const animatedTargets = [
    ...slideRightTargets,
    ...slideUpTargets,
    ...(animateMark ? [mark] : []),
  ].filter(Boolean);

  if (!animatedTargets.length) {
    return;
  }

  if (reducedMotion) {
    gsap.set([...slideRightTargets, ...slideUpTargets], {
      x: 0,
      y: 0,
      opacity: 1,
    });

    if (mark) {
      gsap.set(mark, {
        x: mobileLayout ? SPLIT_MARK_LEFT_MOBILE_X : 0,
        y: 0,
        opacity: mobileLayout ? SPLIT_MARK_LEFT_MOBILE_OPACITY : 1,
      });
    }

    section.classList.add("is-in-view");
    return;
  }

  slideRightTargets.forEach((element) => {
    gsap.set(element, { x: "100vw", opacity: 0 });
  });

  slideUpTargets.forEach((element) => {
    gsap.set(element, { y: TEXT_Y, opacity: 0 });
  });

  if (mark) {
    if (mobileLayout) {
      gsap.set(mark, {
        x: SPLIT_MARK_LEFT_MOBILE_X,
        y: 0,
        opacity: SPLIT_MARK_LEFT_MOBILE_OPACITY,
      });
    } else {
      gsap.set(mark, { x: SPLIT_MARK_X, opacity: 0 });
    }
  }

  createScrollTrigger(section, () => {
    const timeline = gsap.timeline({
      defaults: { duration: DURATION, ease: EASE },
    });
    let step = 0;

    slideRightTargets.forEach((element) => {
      timeline.to(element, { x: 0, opacity: 1 }, step * STAGGER);
      step += 1;
    });

    slideUpTargets.forEach((element) => {
      timeline.to(element, { y: 0, opacity: 1 }, step * STAGGER);
      step += 1;
    });

    if (animateMark) {
      timeline.to(mark, { x: 0, opacity: 1 }, 0);
    }
  });
}

const variants = [
  {
    match: (section) =>
      section.classList.contains("text-section--layout-split-mark-right"),
    init: initSplitMarkRight,
  },
  {
    match: (section) =>
      section.classList.contains("text-section--layout-split-mark-left"),
    init: initSplitMarkLeft,
  },
  {
    match: () => true,
    init: initDefaultTextSection,
  },
];

document.querySelectorAll(".text-section").forEach((section) => {
  const variant = variants.find(({ match }) => match(section));
  variant?.init(section);
});

runInitialRevealChecks();

window.addEventListener(
  "load",
  () => {
    runInitialRevealChecks();
  },
  { once: true },
);
